import React from 'react';
import { useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import DeTimeline from '../../components/datamanagement/DeTimeline';
import DeRightSidebar from '../../components/datamanagement/DeRightSidebar';
import { useDataEntry } from '../../queries/dataEntryQueries';

/**
 * PLPage — Display P&L calculations & summary matching EWAY document layout.
 *
 * Route: /business/pl/:id
 * Active Step in Timeline: 4 (P & L)
 */
const PLPage = () => {
  const { id } = useParams();
  const { data: retrievedData, isLoading, isError } = useDataEntry(id);

  // console.log("Per cft cost : ", retrievedData?.cost)
  // console.log("Per cft cost : ", retrievedData)

  // Extract container rows (userForm or dataEntryForm)
  const rows = Array.isArray(retrievedData?.userForm) && retrievedData.userForm.length > 0
    ? retrievedData.userForm
    : Array.isArray(retrievedData?.dataEntryForm) && retrievedData.dataEntryForm.length > 0
      ? retrievedData.dataEntryForm
      : [];

  const sblcRate = Number(retrievedData?.sblcInterestUsdRate) || 0;
  const isCash = String(retrievedData?.paymentOption || 'Credit').toLowerCase() === 'cash';
  const cost = isCash
    ? (Number(retrievedData?.totalCft) || 0)
    : (Number(retrievedData?.cost) || 0);

  // Calculate total AMT across rows
  const totalAMT = rows.reduce((total, item) => {
    const rate = (Number(item?.avgRate) || 0) + (Number(item?.brijeshComm) || 0);
    const netCbm = Number(item?.netCbm) || 0;
    return total + (rate * netCbm);
  }, 0);

  // Per-container calculations
  let computedNetCbmSum = 0;
  const containerCalculations = rows?.map((ele) => {
    // const rate = (Number(ele?.avgRate) || 0) + (Number(ele?.brijeshComm) || 0);
    const rate = Number(ele?.avgRate) || 0;
    const netCbm = Number(ele?.netCbm) || 0;
    const amt = rate * netCbm;

    // Landing: ((avgRate + brijeshComm) * sblcInterestUsdRate) / 35.315 + cost
    const landing = sblcRate ? ((rate * sblcRate) / 35.315) + cost : cost;
    // const landing = sblcRate ? ((rate * sblcRate) / 35.315) : 0;

    const sellRate = Number(ele?.sellRate) || 0;
    const sellCbm = Number(ele?.sellCbm) || 0;

    // Ratediff: - (landing - sellRate) * (netCbm * 35.315)
    const ratediff = -(landing - sellRate) * (netCbm * 35.315);

    // Cbmdiff: (sellCbm - netCbm) * (sellRate * 35.315)
    const cbmdiff = (sellCbm - netCbm) * (sellRate * 35.315);

    const totalpl = ratediff + cbmdiff;
    computedNetCbmSum += netCbm;

    return {
      ...ele,
      rate,
      amt,
      landing,
      ratediff,
      cbmdiff,
      totalpl,
    };
  });

  const totalSellCbm = containerCalculations.reduce((sum, item) => sum + (Number(item?.sellCbm) || 0), 0);
  const totalRateDiff = containerCalculations.reduce((sum, item) => sum + (Number(item?.ratediff) || 0), 0);
  const totalCbmDiff = containerCalculations.reduce((sum, item) => sum + (Number(item?.cbmdiff) || 0), 0);
  const totalpl = containerCalculations.reduce((sum, item) => sum + (Number(item?.totalpl) || 0), 0);
  const capitalIntExp = 300000;
  const nett = totalpl - capitalIntExp;

  // Excel Export Helper
  const downloadExcel = () => {
    const tableData = containerCalculations.map((item, index) => ({
      'S.no': index + 1,
      'CONTAINER NO': item?.containerNo || '',
      'NOS': item?.pcs || 0,
      'NET CBM': item?.netCbm || 0,
      'RATE': item?.rate || 0,
      'AMT': Number(item?.amt).toFixed(2),
      'CFT/G': item?.avgCftAvgGirth || 0,
      'LANDING': Number(item?.landing).toFixed(0),
      'SELL': item?.sellRate || 0,
      'CBM': item?.sellCbm || 0,
      'RATE DIFF': Number(item?.ratediff).toFixed(2),
      'CBM DIFF': Number(item?.cbmdiff).toFixed(2),
      'TOTAL': Number(item?.totalpl).toFixed(2),
    }));

    const summaryData = [
      { Label: 'BL NO :', Value: retrievedData?.blNo || retrievedData?.billno || 'NULL' },
      { Label: 'TOTAL PCS', Value: retrievedData?.totalPcs || 0 },
      { Label: 'TOTAL NET CBM', Value: computedNetCbmSum || 0 },
      { Label: 'TOTAL AMT', Value: Number(totalAMT).toFixed(2) },
      { Label: 'DISCOUNT', Value: retrievedData?.roundOff ?? 'NULL' },
      { Label: 'INV AMT', Value: retrievedData?.bgAmtAndInterestAmt ? Number(retrievedData.bgAmtAndInterestAmt).toFixed(2) : 'NULL' },
      { Label: 'BRIJESH', Value: retrievedData?.bruesh ?? retrievedData?.brijesh ?? 'NULL' },
      { Label: 'TOTAL SELL CBM', Value: totalSellCbm },
      { Label: 'TOTAL RATE DIFF', Value: Number(totalRateDiff).toFixed(2) },
      { Label: 'TOTAL CBM DIFF', Value: Number(totalCbmDiff).toFixed(2) },
      { Label: 'TOTAL', Value: Number(totalpl).toFixed(2) },
      { Label: 'CAPITAL INT,SALARIES & HOUSE EXP', Value: Number(capitalIntExp).toFixed(2) },
      { Label: 'NETT', Value: Number(nett).toFixed(2) },
    ];

    const tableWorksheet = XLSX.utils.json_to_sheet(tableData, { origin: 'A2' });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, tableWorksheet, 'Table Data');
    XLSX.utils.sheet_add_json(workbook.Sheets['Table Data'], summaryData, { origin: -1 });

    XLSX.writeFile(workbook, `PL_${retrievedData?.blNo || retrievedData?.billno || 'Data'}.xlsx`);
  };

  return (
    <div className="d-flex flex-column h-100 overflow-hidden overflow-y-auto py-2 py-md-3 px-2 px-md-4">
      <div
        className="d-flex flex-column flex-grow-1 mx-auto w-100"
        style={{ maxWidth: '1280px' }}
      >
        {/* ── Step Progress Timeline — Step 4 active (P & L) ── */}
        <div className="flex-shrink-0 pb-1">
          <DeTimeline activeStep={4} />
        </div>

        {/* ── Bootstrap 5 two-column layout ── */}
        <div className="row g-3 flex-grow-1 overflow-hidden">
          {/* ── Main Content Area (Left Column) ── */}
          <div className="col-12 col-lg-9 d-flex flex-column align-items-center">
            {isLoading ? (
              <div className="card shadow-sm w-100 p-5 text-center text-muted">
                <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                Loading P&amp;L document data...
              </div>
            ) : isError ? (
              <div className="card shadow-sm w-100 p-5 text-center text-danger">
                Failed to load P&amp;L document data. Please try again.
              </div>
            ) : (
              <div className="w-100 d-flex flex-column align-items-center">
                {/* ── Document Container ── */}
                <div className="eway-container bg-white border rounded-3 p-3 p-sm-4 shadow-sm w-100 d-flex flex-column" style={{ minHeight: 'calc(100vh - 180px)' }}>
                  {/* File Name & BL NO Header matching EWAY & legacy software */}
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-3 border-bottom pb-2">
                    <div className="d-flex align-items-center gap-2">
                      <span className="eway-meta-label fs-6 fw-bold text-secondary">BL NO :</span>
                      <span className="eway-meta-value fs-6 fw-bold text-dark font-monospace text-break">
                        {retrievedData?.blNo || retrievedData?.billno || '-'}
                      </span>
                    </div>
                    <span className="fw-bold fs-5 text-primary font-monospace text-break">
                      {retrievedData?.fileName || '-'}
                    </span>
                  </div>

                  {/* Containers Table with custom horizontal scrollbar */}
                  <div className="table-responsive border rounded-2 pl-table-container mb-3" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <table className="table table-bordered align-middle text-center mb-0 text-nowrap" style={{ minWidth: '1050px' }}>
                      <thead className="gtss-bg-primary text-white text-uppercase">
                        <tr>
                          <th className="text-center py-2 px-2" style={{ width: '40px' }}>S.no</th>
                          <th className="text-center py-2 px-2">CONTAINER NO</th>
                          <th className="text-center py-2 px-2">NOS</th>
                          <th className="text-center py-2 px-2">NET CBM</th>
                          <th className="text-center py-2 px-2">RATE</th>
                          <th className="text-center py-2 px-2">AMT</th>
                          <th className="text-center py-2 px-2">CFT/G</th>
                          <th className="text-center py-2 px-2">LANDING</th>
                          <th className="text-center py-2 px-2">SELL</th>
                          <th className="text-center py-2 px-2">CBM</th>
                          <th className="text-center py-2 px-2">RATE DIFF</th>
                          <th className="text-center py-2 px-2">CBM DIFF</th>
                          <th className="text-center py-2 px-2">TOTAL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {containerCalculations.length > 0 ? (
                          containerCalculations?.map((ele, index) => (
                            <tr key={index}>
                              <td className="text-center py-1 px-2 fw-semibold">{index + 1}</td>
                              <td className="text-center py-1 px-2 font-monospace fw-bold text-primary">{ele?.containerNo || '-'}</td>
                              <td className="text-center py-1 px-2">{ele?.pcs || 0}</td>
                              <td className="text-center py-1 px-2 font-monospace">{ele?.netCbm || 0}</td>
                              <td className="text-center py-1 px-2 font-monospace">{ele?.rate || 0}</td>
                              <td className="text-center py-1 px-2 font-monospace fw-semibold">{Number(ele?.amt).toFixed(2)}</td>
                              <td className="text-center py-1 px-2 font-monospace">{ele?.avgCftAvgGirth || 0}</td>
                              <td className="text-center py-1 px-2 font-monospace">{Number(ele?.landing).toFixed(0)}</td>
                              <td className="text-center py-1 px-2 font-monospace">{ele?.sellRate || 0}</td>
                              <td className="text-center py-1 px-2 font-monospace">{ele?.sellCbm || 0}</td>
                              <td className={`text-center py-1 px-2 font-monospace ${ele?.ratediff < 0 ? 'text-danger fw-semibold' : 'text-success'}`}>
                                {Number(ele?.ratediff).toFixed(2)}
                              </td>
                              <td className={`text-center py-1 px-2 font-monospace ${ele?.cbmdiff < 0 ? 'text-danger fw-semibold' : 'text-success'}`}>
                                {Number(ele?.cbmdiff).toFixed(2)}
                              </td>
                              <td className={`text-center py-1 px-2 font-monospace fw-bold ${ele?.totalpl < 0 ? 'text-danger' : 'text-success'}`}>
                                {Number(ele?.totalpl).toFixed(2)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="13" className="text-center py-4 text-muted">
                              No container records found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Summary Totals List (Matching EWAY / Legacy Software styling) */}
                  <div className="d-flex flex-column gap-2 mt-auto pt-2 border-top">
                    {/* <div className="row py-1 align-items-center border-bottom border-light">
                      <span className="col-6 col-sm-5 text-uppercase fw-bold text-secondary small">TOTAL PCS :</span>
                      <span className="col-6 col-sm-7 font-monospace fw-bold text-dark">{retrievedData?.totalPcs ?? 0}</span>
                    </div>

                    <div className="row py-1 align-items-center border-bottom border-light">
                      <span className="col-6 col-sm-5 text-uppercase fw-bold text-secondary small">TOTAL NET CBM :</span>
                      <span className="col-6 col-sm-7 font-monospace fw-bold text-dark">{Number(computedNetCbmSum).toFixed(3)}</span>
                    </div>

                    <div className="row py-1 align-items-center border-bottom border-light">
                      <span className="col-6 col-sm-5 text-uppercase fw-bold text-secondary small">TOTAL AMT :</span>
                      <span className="col-6 col-sm-7 font-monospace fw-bold text-dark">{Number(totalAMT).toFixed(2)}</span>
                    </div>

                    <div className="row py-1 align-items-center border-bottom border-light">
                      <span className="col-6 col-sm-5 text-uppercase fw-bold text-secondary small">DISCOUNT :</span>
                      <span className="col-6 col-sm-7 font-monospace fw-bold text-dark">{retrievedData?.roundOff ?? 'NULL'}</span>
                    </div>

                    <div className="row py-1 align-items-center border-bottom border-light">
                      <span className="col-6 col-sm-5 text-uppercase fw-bold text-secondary small">INV AMT :</span>
                      <span className="col-6 col-sm-7 font-monospace fw-bold text-dark">{retrievedData?.bgAmtAndInterestAmt ? Number(retrievedData.bgAmtAndInterestAmt).toFixed(2) : 'NULL'}</span>
                    </div>

                    <div className="row py-1 align-items-center border-bottom border-light">
                      <span className="col-6 col-sm-5 text-uppercase fw-bold text-secondary small">BRIJESH :</span>
                      <span className="col-6 col-sm-7 font-monospace fw-bold text-dark">{retrievedData?.bruesh ?? retrievedData?.brijesh ?? 'NULL'}</span>
                    </div>

                    <div className="row py-1 align-items-center border-bottom border-light">
                      <span className="col-6 col-sm-5 text-uppercase fw-bold text-secondary small">TOTAL SELL CBM :</span>
                      <span className="col-6 col-sm-7 font-monospace fw-bold text-dark">{Number(totalSellCbm).toFixed(3)}</span>
                    </div>

                    <div className="row py-1 align-items-center border-bottom border-light">
                      <span className="col-6 col-sm-5 text-uppercase fw-bold text-secondary small">TOTAL RATE DIFF :</span>
                      <span className={`col-6 col-sm-7 font-monospace fw-bold ${totalRateDiff < 0 ? 'text-danger' : 'text-success'}`}>{Number(totalRateDiff).toFixed(2)}</span>
                    </div>

                    <div className="row py-1 align-items-center border-bottom border-light">
                      <span className="col-6 col-sm-5 text-uppercase fw-bold text-secondary small">TOTAL CBM DIFF :</span>
                      <span className={`col-6 col-sm-7 font-monospace fw-bold ${totalCbmDiff < 0 ? 'text-danger' : 'text-success'}`}>{Number(totalCbmDiff).toFixed(2)}</span>
                    </div> */}

                    <div className="d-flex justify-content-end align-items-center gap-2 py-1">
                      <span className="text-uppercase fw-bold text-secondary small">TOTAL :</span>
                      <span className={`font-monospace fw-bold fs-5 ${totalpl < 0 ? 'text-danger' : 'text-success'}`}>{Number(totalpl).toFixed(2)}</span>
                    </div>

                    {/* <div className="row py-1 align-items-center border-bottom border-light">
                      <span className="col-6 col-sm-5 text-uppercase fw-bold text-secondary small">CAPITAL INT,SALARIES &amp; HOUSE EXP :</span>
                      <span className="col-6 col-sm-7 font-monospace fw-bold text-dark">{Number(capitalIntExp).toFixed(2)}</span>
                    </div>

                    <div className="row py-2 align-items-center bg-primary-subtle rounded px-2 mt-1 border border-primary">
                      <span className="col-6 col-sm-5 text-uppercase fw-bold text-primary">NETT :</span>
                      <span className={`col-6 col-sm-7 font-monospace fw-bold fs-6 ${nett < 0 ? 'text-danger' : 'text-success'}`}>{Number(nett).toFixed(2)}</span>
                    </div> */}
                  </div>
                </div>

                {/* Download Button */}
                {/* <div className="my-4 text-center">
                  <button
                    type="button"
                    className="btn btn-orange text-uppercase fw-bold px-4 py-2"
                    onClick={downloadExcel}
                    disabled={isLoading}
                  >
                    📥 Download Excel
                  </button>
                </div> */}
              </div>
            )}
          </div>

          {/* ── Right Sidebar Column ── */}
          <div className="col-12 col-lg-3">
            <DeRightSidebar id={id} activeStep={4} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PLPage;
